import {Box, Flex, Span} from 'exsportia-components'
import {DateTime} from 'luxon'
import React, {FC, useCallback, useMemo} from 'react'

import {SubscriptionBooking} from '../../../../api/subscription-bookings/types'
import {PriceTypeEnum, SubscriptionActivity} from '../../../../api/subscriptions/types'
import {useVenue} from '../../../../api/venue/hooks'
import {FULL_CANCEL_POLICY_OPTIONS_MAP} from '../../../../constants/cancel-policy'
import {useServiceOptions} from '../../../../services/service-options/hooks'
import {Chip} from '../../../../ui/components/chip/chip'
import {typeStylesMap} from '../../../../ui/components/chip/settings'
import {DividerWithContent} from '../../../../ui/components/divider-with-content/divider-with-content'
import {GradientLoader} from '../../../../ui/components/gradient-loader/gradient-loader'
import {LabeledValueRow} from '../../../../ui/components/key-value-row/labeled-value-row'
import {LabeledSection} from '../../../../ui/components/labeled-section/labeled-section'
import {useLocale, useLocaleType} from '../../../../utils/i18n'
import {NO_USER_PHOTO} from '../../../../utils/remote-content'
import {PeriodLabel} from '../benefit-features'

type PriceDiscount = {
  price?: number;
  l: useLocaleType
}

type DiscountTextType = {
  [key in PriceTypeEnum]: (params: PriceDiscount) => JSX.Element
}


export const priceTypeComponents: DiscountTextType = {
  [PriceTypeEnum.FREE]: ({l}) => (
    <Span variant={'body'} color={'success'}>{l('free')}</Span>
  ),
  [PriceTypeEnum.BY_PERCENTAGE]: ({price}) => (
    <Span variant={'body'} color={'success'}>{`-${price}%`}</Span>
  )
}

const iconBoxSettings = {
  width: '16px',
  height: '16px',
  borderRadius: '8px',
  backgroundSize: 'cover',
}

type Props = {
  benefit: SubscriptionActivity
  subscriptionBooking?: SubscriptionBooking | null,
}

export const SubscriptionBenefitsModal: FC<Props> = ({benefit, subscriptionBooking}) => {
  const {l} = useLocale()
  const {data: _venue} = useVenue()
  const {allMap} = useServiceOptions()
  const startDate = subscriptionBooking?.startDate
    ? DateTime.fromMillis(subscriptionBooking.startDate).toLocaleString(DateTime.DATE_MED)
    : null
  const endDate = subscriptionBooking?.endDate
    ? DateTime.fromMillis(subscriptionBooking.endDate).toLocaleString(DateTime.DATE_MED)
    : null
  const DiscountComponent = useMemo(
    () => priceTypeComponents[benefit.priceType],
    [benefit.priceType, l]
  )
  const getChipColor = useCallback((isSpace?: boolean): keyof typeof typeStylesMap => {
    if (benefit.groupType === 'all' || benefit.groupType === 'class') {
      return 'class'
    }
    if (isSpace) {
      return 'justTheSpace'
    }
    return 'service'
  }, [])

  return (
    <Box pb={'16px'}>
      <LabeledSection label={l('included', {caseAction: 'capitalize'})}>
        <Flex
          my={'8px'}
          flexWrap={'wrap'}
          gap={'8px'}
        >
          {
            benefit.entities.map((entity, index) => {
              if (entity.sportType && entity.isAll) {
                return (
                  <Chip
                    key={index}
                    icon={allMap[entity.sportType].icon}
                    text={`${l('all')} ${l(allMap[entity.sportType].label)}`}
                    styleType={getChipColor(entity.areaSpace)}
                  />
                )
              }
              return (
                <Chip
                  key={index}
                  icon={entity.sportType ? allMap[entity.sportType].icon : 'usersOutline24'}
                  text={entity.name}
                  styleType={getChipColor(entity.areaSpace)}
                />
              )
            })
          }
        </Flex>
      </LabeledSection>
      <DividerWithContent />
      {
        subscriptionBooking
        && (
          <>
            <LabeledValueRow label={l('period')}>
              {
                (startDate && endDate)
                  ? `${startDate || ''} - ${endDate || ''}`
                  : (<GradientLoader style={{width: '150px'}}/>)
              }
            </LabeledValueRow>
            <DividerWithContent />
          </>
        )
      }
      <LabeledValueRow
        label={l('available', {caseAction: 'capitalize'})}
        align={'flex-start'}
      >
        {<PeriodLabel benefit={benefit} />}
      </LabeledValueRow>
      <DividerWithContent />
      {
        benefit.useForParticipants
        && (
          <>
            <LabeledValueRow
              label={l('shareable', {caseAction: 'capitalize'})}
              align={'flex-start'}
            >
              <Span variant={'footnote'} color={'success'}>
                {l('yes', {caseAction: 'capitalize'})}
              </Span>
            </LabeledValueRow>
            <DividerWithContent />
          </>
        )
      }
      {
        benefit.coverEquipment
        && (
          <>
            <LabeledValueRow
              label={l('equipment_included', {caseAction: 'capitalize'})}
              align={'flex-start'}
            >
              <Span variant={'footnote'} color={'success'}>
                {l('yes', {caseAction: 'capitalize'})}
              </Span>
            </LabeledValueRow>
            <DividerWithContent />
          </>
        )
      }
      {
        (benefit.initialDiscount || 0) > 0 && benefit.initialDiscountEnabled
        && (
          <>
            <LabeledValueRow
              label={l('initialDiscount', {caseAction: 'capitalize'})}
              align={'flex-start'}
            >
              <Span variant={'footnote'} color={'success'}>{benefit.initialDiscount}%</Span>
            </LabeledValueRow>
            <DividerWithContent />
          </>
        )
      }
      <LabeledValueRow
        label={l('discount', {caseAction: 'capitalize'})}
        align={'flex-start'}
      >
        <DiscountComponent price={benefit.discount} l={l}/>
      </LabeledValueRow>
      {
        benefit.cancelPolicy
        && (
          <>
            <DividerWithContent />
            <Box mt={'8px'}>
              <LabeledSection label={l('cancelPolicy', {caseAction: 'capitalize'})}>
                {FULL_CANCEL_POLICY_OPTIONS_MAP[benefit.cancelPolicy]
                  ? l(FULL_CANCEL_POLICY_OPTIONS_MAP[benefit.cancelPolicy].description)
                  : benefit.cancelPolicy}
              </LabeledSection>
            </Box>
          </>
        )
      }
    </Box>
  )
}
